import React from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { MapPin, Phone, Mail, MessageCircle, Clock, ShieldCheck, Building2 } from "lucide-react";

const OFFICE = [16.8602, 78.3009]; // Kadthal, Telangana
const pin = L.divIcon({ className: "", html: '<div class="price-pin">Dotlands.in</div>', iconAnchor: [40, 14] });

export default function ContactAbout() {
  const { siteConfig } = useAuth();
  const address = siteConfig?.address || "Naik Complex, Kadthal, 509358, Telangana, India";
  const phone = siteConfig?.phone || "9392108692";
  const whatsapp = siteConfig?.whatsapp || "919392108692";
  const email = siteConfig?.email || "dotlandsrealty@gmail.com";

  const cards = [
    { icon: MapPin, label: "Office Address", value: address, href: null },
    { icon: Phone, label: "Call Us", value: `+91 ${phone}`, href: `tel:+91${phone}` },
    { icon: MessageCircle, label: "WhatsApp", value: `+91 ${phone}`, href: `https://wa.me/${whatsapp}` },
    { icon: Mail, label: "Email", value: email, href: `mailto:${email}` },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto py-12">
      <Seo
        title="Contact Dotlands Realty | Real Estate Office in Kadthal, Telangana"
        description="Contact Dotlands Realty for verified agricultural land, plots and homes in Telangana. Call, WhatsApp or email our Kadthal office. Building Dreams. Creating Value."
        path="/contact"
      />
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8A24C]/15 text-[#A8863A] text-xs font-bold uppercase tracking-wide"><Building2 className="w-3.5 h-3.5" /> About & Contact</span>
        <h1 className="text-3xl sm:text-5xl font-heading font-extrabold text-[#0E3B2E] mt-4">Dotlands.in</h1>
        <p className="text-[#C8A24C] font-semibold italic mt-2">Building Dreams. Creating Value.</p>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Dotlands.in is your trusted partner for verified agricultural land, open plots and homes across Telangana and India.
          We connect genuine buyers and sellers with transparent, direct deals — every listing is verified by our team before it goes live.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {cards.map((c) => {
          const Inner = (
            <>
              <div className="w-11 h-11 rounded-xl bg-[#0E3B2E] flex items-center justify-center mb-3"><c.icon className="w-5 h-5 text-[#C8A24C]" /></div>
              <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{c.label}</div>
              <div className="text-slate-800 font-medium mt-1 break-words">{c.value}</div>
            </>
          );
          return c.href ? (
            <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
              data-testid={`contact-${c.label.toLowerCase().split(" ")[0]}`}
              className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#C8A24C] hover:shadow-md transition-all">
              {Inner}
            </a>
          ) : (
            <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="contact-address">{Inner}</div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 h-80 rounded-2xl overflow-hidden border border-slate-200">
          <MapContainer center={OFFICE} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={OFFICE} icon={pin} />
          </MapContainer>
        </div>
        <div className="bg-[#0E3B2E] text-white rounded-2xl p-6 flex flex-col justify-center">
          <Clock className="w-8 h-8 text-[#C8A24C] mb-3" />
          <h3 className="font-heading font-bold text-lg">Business Hours</h3>
          <p className="text-slate-300 text-sm mt-2">Monday – Saturday<br />9:30 AM – 7:30 PM</p>
          <div className="flex items-center gap-2 mt-4 text-sm text-slate-300"><ShieldCheck className="w-4 h-4 text-[#C8A24C]" /> Every listing verified by Dotlands</div>
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center justify-center gap-2 bg-[#C8A24C] text-[#0E3B2E] font-bold rounded-full px-5 py-2.5 hover:bg-[#A8863A]">
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
